import React, { useEffect, useRef, useState } from "react";
import { getFirstValidImageUrl } from "../../../utils/recentlyViewedProducts";

const MAX_IMAGE_RETRIES = 2;
const SIGNED_URL_QUERY_PATTERN = /[?&](x-amz-signature|signature|policy|key-pair-id|googleaccessid)=/i;

const addRetryParam = (src, attempt) => {
  if (!src || src.startsWith("data:") || src.startsWith("blob:") || SIGNED_URL_QUERY_PATTERN.test(src)) {
    return src;
  }

  const [urlWithoutHash, hash = ""] = src.split("#");
  const separator = urlWithoutHash.includes("?") ? "&" : "?";

  return `${urlWithoutHash}${separator}rv_retry=${attempt}_${Date.now()}${hash ? `#${hash}` : ""}`;
};

const RecentlyViewedProductImage = ({ src, alt, style }) => {
  const baseSrc = getFirstValidImageUrl(src);
  const [imageSrc, setImageSrc] = useState(baseSrc);
  const [isLoaded, setIsLoaded] = useState(false);
  const [retryVersion, setRetryVersion] = useState(0);
  const imageRef = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);

  useEffect(() => {
    retryCountRef.current = 0;
    setRetryVersion(0);
    setImageSrc(baseSrc);
    setIsLoaded(false);

    return () => {
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
      }
    };
  }, [baseSrc]);

  useEffect(() => {
    const image = imageRef.current;

    if (image?.complete && image.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [imageSrc]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setIsLoaded(false);

    if (!baseSrc || retryCountRef.current >= MAX_IMAGE_RETRIES) {
      return;
    }

    retryCountRef.current += 1;
    const retryAttempt = retryCountRef.current;

    retryTimerRef.current = window.setTimeout(() => {
      setImageSrc(addRetryParam(baseSrc, retryAttempt));
      setRetryVersion((version) => version + 1);
    }, retryAttempt * 300);
  };

  return (
    <img
      key={`${imageSrc || "empty"}-${retryVersion}`}
      ref={imageRef}
      src={imageSrc || undefined}
      alt={alt || "Product"}
      decoding="async"
      loading="eager"
      onLoad={handleLoad}
      onError={handleError}
      style={{
        ...style,
        opacity: isLoaded ? 1 : 0,
        transition: "opacity 160ms ease",
      }}
    />
  );
};

export default RecentlyViewedProductImage;
